import { useEffect, useRef } from "react";
import type { Route } from "./+types/for-manufacturers";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { GoldMesh } from "~/components/marketing/svg/GoldMesh";
import { ProcessFlow } from "~/components/marketing/svg/ProcessFlow";
import { JewelryIcon, type JewelryIconType } from "~/components/marketing/svg/JewelryIcons";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  const t = translations[locale].forManufacturers.meta;
  return [
    { title: t.title },
    { name: "description", content: t.description },
    ...socialMeta({ title: t.title, description: t.description, pathname: "/for-manufacturers", locale }),
  ];
}

function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

export default function ForManufacturersPage() {
  const { t } = useT();
  const h = t.forManufacturers;

  const contentsRef = useReveal();
  const workflowRef = useReveal(0.1);
  const ephRef = useReveal(0.08);
  const ctaRef = useReveal();

  const ORDER_ICONS: JewelryIconType[] = ["cad-file", "production", "eco-plate", "approval"];

  return (
    <MarketingLayout>

      {/* ══ HERO ══ */}
      <section className="px-6 lg:px-12 pt-16 pb-14 lg:pt-24 lg:pb-20 relative">
        <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={i} x1={`${(i + 1) * 9}%`} y1="0" x2={`${(i + 1) * 9}%`} y2="100%" stroke="oklch(0.085 0.004 50)" strokeWidth="1" opacity="0.02" />
          ))}
        </svg>

        <p className="issue-label mb-6 relative" style={{ fontFamily: "var(--font-sans)" }}>{h.hero.overline}</p>

        <h1 className="font-display font-light leading-[0.92] tracking-tight text-foreground max-w-4xl relative" style={{ fontSize: "clamp(3.5rem, 8vw, 7.5rem)" }}>
          {h.hero.h1Line1}
          <br />
          {h.hero.h1Line2}
          <br />
          <em className="not-italic" style={{ fontWeight: 300, opacity: 0.42 }}>{h.hero.h1Line3}</em>
        </h1>

        <div className="mt-12 grid lg:grid-cols-[1fr_420px] items-start gap-8 relative">
          <div className="space-y-8">
            <p className="font-display font-light text-muted-foreground" style={{ fontSize: "clamp(1.05rem, 1.8vw, 1.4rem)", lineHeight: 1.5, maxWidth: "44ch" }}>{h.hero.tagline}</p>
            <a href="/sign-in?redirect=/trade/catalog" className="inline-block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.hero.cta}</a>
          </div>
          <div className="hidden lg:block relative overflow-hidden bg-muted" style={{ aspectRatio: "4/3" }}>
            <img src="https://images.unsplash.com/photo-1600721391776-b5cd0e0048f9?auto=format&fit=crop&w=420&q=80" alt="" aria-hidden="true" className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02]" style={{ filter: "grayscale(0.12) contrast(1.06) saturate(0.80)" }} loading="lazy" />
          </div>
        </div>
      </section>

      {/* ── Stat bar ── */}
      <div className="px-6 lg:px-12">
        <div className="rule" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
          {([
            [h.stats.cadReady, h.stats.everyOrder],
            [h.stats.phases, h.stats.milestone],
            [h.stats.ecoPlating, h.stats.ephCertified],
            [h.stats.fullTrace, h.stats.clientVis],
          ] as [string, string][]).map(([value, label], i) => (
            <div key={label} className={`py-5 ${i > 0 ? "pl-6 border-l border-border" : ""}`}>
              <p className="font-display font-light text-foreground leading-none mb-1" style={{ fontSize: "clamp(1rem, 1.8vw, 1.4rem)" }}>{value}</p>
              <p className="issue-label" style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}>{label}</p>
            </div>
          ))}
        </div>
        <div className="rule" />
      </div>

      {/* ══ WHAT EACH ORDER INCLUDES ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-20">
        <div ref={contentsRef} className="reveal">
          <div className="flex items-baseline justify-between mb-10">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.orderContents.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.orderContents.subtitle}</span>
          </div>
          <div className="grid lg:grid-cols-4 gap-0">
            {h.orderContents.items.map((item, i) => (
              <div key={i} className="relative">
                {i > 0 ? <div className="hidden lg:block absolute inset-y-0 left-0 w-px bg-border" /> : null}
                <div className="py-8" style={{ paddingLeft: i > 0 ? "var(--col-pad)" : undefined, paddingRight: i < h.orderContents.items.length - 1 ? "var(--col-pad)" : undefined }}>
                  <div className="rule mb-5" />
                  <JewelryIcon type={ORDER_ICONS[i]} size={40} className="mb-4 opacity-50" />
                  <span className="font-display font-light select-none block mb-4" style={{ fontSize: "clamp(1.8rem, 2.5vw, 2.4rem)", lineHeight: 1, color: "var(--primary)", opacity: 0.5 }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display font-normal text-foreground leading-tight mb-3" style={{ fontSize: "clamp(1.1rem, 1.6vw, 1.35rem)" }}>{item.heading}</h3>
                  <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.8rem", maxWidth: "28ch" }}>{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ WORKFLOW PHASES ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-20">
        <div ref={workflowRef} className="reveal">
          <div className="flex items-baseline justify-between mb-10">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.workflow.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.workflow.subtitle}</span>
          </div>
          <ProcessFlow steps={6} className="mb-8 hidden lg:block" />
          <div className="max-w-3xl space-y-0">
            {h.workflow.phases.map(([phase, description], i) => (
              <div key={phase}>
                <div className="rule" />
                <div className="py-5 grid sm:grid-cols-[48px_180px_1fr] gap-2 sm:gap-6 items-baseline">
                  <span className="font-display font-light select-none" style={{ fontSize: "clamp(1.2rem, 2vw, 1.6rem)", color: "var(--primary)", opacity: 0.5 }}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{phase}</span>
                  <p className="text-muted-foreground" style={{ fontSize: "0.82rem", lineHeight: 1.5 }}>{description}</p>
                </div>
              </div>
            ))}
            <div className="rule" />
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ EPH SPOTLIGHT ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24 relative overflow-hidden">
        <GoldMesh opacity={0.1} />
        <div ref={ephRef} className="reveal relative">
          <div className="flex items-baseline justify-between mb-10">
            <span className="issue-label" style={{ fontFamily: "var(--font-sans)" }}>{h.eph.overline}</span>
            <span className="issue-label hidden lg:block" style={{ fontFamily: "var(--font-sans)", opacity: 0.35 }}>{h.eph.location}</span>
          </div>
          <div className="grid lg:grid-cols-[1fr_400px] gap-10 items-end mb-14">
            <div className="relative leading-none select-none">
              <a href="https://eph.eco/" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-70"><span className="font-display font-light text-foreground leading-none" style={{ fontSize: "clamp(5rem, 14vw, 12rem)", letterSpacing: "-0.02em", lineHeight: 0.88 }}>EPH</span></a>
            </div>
            <div className="pb-2 space-y-4">
              <JewelryIcon type="eco-plate" size={48} className="opacity-55 mb-1" />
              <p className="font-display font-light text-foreground" style={{ fontSize: "clamp(1rem, 1.6vw, 1.3rem)", lineHeight: 1.45 }}>{h.eph.headline}</p>
              <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.82rem" }}>{h.eph.body}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0">
            {h.eph.attrs.map((attr, i) => (
              <div key={attr.label} className="relative">
                {i > 0 ? <div className="hidden sm:block absolute inset-y-0 left-0 w-px bg-border" /> : null}
                <div className="py-7" style={{ paddingLeft: i > 0 ? "var(--col-pad-sm)" : undefined, paddingRight: i < h.eph.attrs.length - 1 ? "var(--col-pad-sm)" : undefined }}>
                  <div className="rule mb-5" />
                  <span className="issue-label block mb-3" style={{ fontFamily: "var(--font-sans)" }}>{attr.label}</span>
                  <p className="text-muted-foreground leading-relaxed" style={{ fontSize: "0.78rem" }}>{attr.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Editorial production images ── */}
      <div className="px-6 lg:px-12 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {([ ["1543294001-f7cd5d7fb516","center center"], ["1629224316810-9d8805b95e76","center center"] ] as [string,string][]).map(([id, pos], i) => (
            <div key={i} className="relative overflow-hidden bg-muted" style={{ aspectRatio: "16/9" }}>
              <img src={`https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=640&q=80`} alt="" aria-hidden="true" className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]" style={{ objectPosition: pos, filter: "grayscale(0.12) contrast(1.06) saturate(0.80)" }} loading="lazy" />
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ CTA ══ */}
      <section className="px-6 lg:px-12 py-20 lg:py-28">
        <div ref={ctaRef} className="reveal grid lg:grid-cols-[1fr_auto] items-end gap-8">
          <div>
            <span className="issue-label block mb-5" style={{ fontFamily: "var(--font-sans)" }}>{h.cta.overline}</span>
            <h2 className="font-display font-light leading-[0.92] tracking-tight text-foreground" style={{ fontSize: "clamp(2.8rem, 6vw, 5.5rem)" }}>
              {h.cta.heading.split("\n").map((line, i) => (
                <span key={i}>{i === 0 ? line : <em className="not-italic" style={{ fontWeight: 300, opacity: 0.45 }}>{line}</em>}{i === 0 && <br />}</span>
              ))}
            </h2>
          </div>
          <div className="pb-2 space-y-4">
            <a href="/sign-in?redirect=/trade/catalog" className="block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors text-center" style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}>{h.cta.primary}</a>
            <a href="/partner-application" className="block text-center issue-label hover:text-foreground transition-colors" style={{ fontFamily: "var(--font-sans)", opacity: 0.45 }}>{h.cta.secondary}</a>
          </div>
        </div>
      </section>

    </MarketingLayout>
  );
}
